import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserStore } from '@/store/userStore';
import { supabase } from '@/lib/supabase';
import { Loader } from '@/components/Loader';
import { BottomNav } from '@/components/BottomNav';
import { UpdateBanner } from '@/components/UpdateBanner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LegalAcceptScreen } from '@/components/LegalAcceptScreen';
import { useLegalAcceptance } from '@/hooks/useLegalAcceptance';
import { GlobalAudioPlayer } from '@/components/GlobalAudioPlayer';
import { MiniPlayer } from '@/components/MiniPlayer';
import { AchievementProvider } from '@/components/AchievementProvider';
import Onboarding from '@/screens/Onboarding';
import Dashboard from '@/screens/Dashboard';
import AddMeal from '@/screens/AddMeal';
import Diary from '@/screens/Diary';
import Stats from '@/screens/Stats';
import MoodMonth from '@/screens/MoodMonth';
import Profile from '@/screens/Profile';
import LiveChat from '@/screens/LiveChat';
import Paywall from '@/screens/Paywall';
import Admin from '@/screens/Admin';
import EatingTest from '@/screens/EatingTest';
import StartReview from '@/screens/StartReview';
import PortraitFull from '@/screens/PortraitFull';
import { getLaunch, FEATURE_ROUTE } from '@/lib/launch';
import Login from '@/screens/Login';
import Achievements from '@/screens/Achievements';
import TalkToSlava from '@/screens/TalkToSlava';
import { RelaxCategory } from '@/screens/relax/RelaxCategory';
import { RelaxPlayer } from '@/screens/relax/RelaxPlayer';
import BreathingIntroScreen from '@/screens/relax/BreathingIntroScreen';
import SelfSupport from '@/screens/relax/SelfSupport';
import BreathingExerciseScreen from '@/screens/relax/BreathingExerciseScreen';
import FavoriteList from '@/screens/FavoriteList';
import Micronutrients from '@/screens/Micronutrients';
import WorkoutPlanScreen from '@/screens/workout/WorkoutPlanScreen';
import WorkoutSessionScreen from '@/screens/workout/WorkoutSessionScreen';
import WorkoutLogScreen from '@/screens/workout/WorkoutLogScreen';
import WorkoutPlanDetailScreen from '@/screens/workout/WorkoutPlanDetailScreen';
import CustomWorkoutScreen from '@/screens/workout/CustomWorkoutScreen';
import PathScreen from '@/screens/PathScreen';
import StepScreen from '@/screens/StepScreen';
import PointAScreen from '@/screens/PointAScreen';
import MilestoneScreen from '@/screens/MilestoneScreen';
import Invite from '@/screens/Invite';
import WeekReview from '@/screens/WeekReview';
import PathPoem from '@/screens/PathPoem';
import Gift from '@/screens/Gift';
import GiftWelcome, { GIFT_WELCOME_KEY, readGiftWelcome } from '@/screens/GiftWelcome';

// Код подарка запоминаем СРАЗУ при загрузке страницы: получатель может быть
// ещё не зарегистрирован, а после входа приложение перерисуется и параметр
// из адреса может не дожить до момента, когда появится профиль.
const PENDING_GIFT_KEY = 'pending_gift_code';
try {
  const c = new URLSearchParams(window.location.search).get('gift');
  if (c) sessionStorage.setItem(PENDING_GIFT_KEY, c);
} catch {
  // приватный режим / отключённое хранилище — молча работаем без запоминания
}

export default function App() {
  useAuth();
  const { profile, loading } = useUserStore();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader label="Готовим Slava Health ..." />
      </div>
    );
  }
  
  const isTelegramMiniApp = typeof window !== 'undefined' && 
    !!(window as any).Telegram?.WebApp?.initData;
  
  if (!profile) {
    if (isTelegramMiniApp) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Loader label="Авторизация через Telegram..." />
        </div>
      );
    }
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<Login />} />
        </Routes>
      </BrowserRouter>
    );
  }
  
  if (!profile.onboarded) {
    return (
      <BrowserRouter>
        <PromoCodeApplier />
      <ReferralApplier />
      <GiftApplier />
        <Routes>
          <Route path="*" element={<Onboarding />} />
        </Routes>
      </BrowserRouter>
    );
  }
  
  return (
    <BrowserRouter>
      <PromoCodeApplier />
      <ReferralApplier />
      <GiftApplier />
      <LegalGate>
        <SubscriptionGuard />
        <LaunchRedirect />
        <LaunchAttribution />
        <GiftWelcomeGate />
        <div className="relative z-10">
          <RoutedErrorBoundary>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/add" element={<AddMeal />} />
            <Route path="/diary" element={<Diary />} />
            <Route path="/stats" element={<Stats />} />
            <Route path="/chat" element={<LiveChat />} />
            <Route path="/paywall" element={<Paywall />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/invite" element={<Invite />} />
            <Route path="/gift" element={<Gift />} />
            <Route path="/gift/welcome" element={<GiftWelcome />} />
            <Route path="/week-review" element={<WeekReview />} />
            <Route path="/trend" element={<MoodMonth />} />
            <Route path="/path/poem" element={<PathPoem />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/test" element={<EatingTest />} />
            <Route path="/start-review" element={<StartReview />} />
            <Route path="/portrait" element={<PortraitFull />} />
            <Route path="/onboarding-test" element={<Onboarding testMode />} />
            <Route path="/achievements" element={<Achievements />} />
            <Route path="/talk" element={<TalkToSlava />} />
            <Route path="/relax/category/:slug" element={<RelaxCategory />} />
            <Route path="/relax/player/:slug" element={<RelaxPlayer />} />
            <Route path="/relax/self" element={<SelfSupport />} />
            <Route path="/breathing/:technique" element={<BreathingIntroScreen />} />
            <Route path="/breathing/:technique/run" element={<BreathingExerciseScreen />} />
            <Route path="/favorites" element={<FavoriteList />} />
            <Route path="/micronutrients" element={<Micronutrients />} />
            <Route path="/path" element={<PathScreen />} />
            <Route path="/path/start" element={<PointAScreen />} />
            <Route path="/path/milestone" element={<MilestoneScreen />} />
            <Route path="/path/step/:day" element={<StepScreen />} />
            <Route path="/workout/create" element={<WorkoutPlanScreen />} />
            <Route path="/workout/custom" element={<CustomWorkoutScreen />} />
            <Route path="/workout/plan/:planId" element={<WorkoutPlanDetailScreen />} />
            <Route path="/workout/session/:planId/:sessionIdx" element={<WorkoutSessionScreen />} />
            <Route path="/workout/log/:sessionId" element={<WorkoutLogScreen />} />
            <Route path="/path" element={<PathScreen />} />
            <Route path="/path/start" element={<PointAScreen />} />
            <Route path="/path/milestone" element={<MilestoneScreen />} />
            <Route path="/path/step/:day" element={<StepScreen />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </RoutedErrorBoundary>
        </div>
        
        {/* 🆕 Глобальный аудио-плеер — рендерится один раз, никогда не размонтируется */}
        <GlobalAudioPlayer />
        
        {/* 🆕 Видимый мини-плеер над BottomNav */}
        <MiniPlayer />
        
        {/* 🆕 Модалка разблокировки ачивок */}
        <AchievementProvider />
        
        {/* Замечает новую сборку и предлагает обновиться. У index.html нет
            Cache-Control ни на одном хосте, а конфиг Timeweb не наш — поэтому
            решаем в приложении. За два дня выкаченный код дважды не доезжал
            до человека, и это выглядело как «ничего не изменилось». */}
        <UpdateBanner />
        <BottomNav />
      </LegalGate>
    </BrowserRouter>
  );
}

// Предохранитель, привязанный к маршруту: при переходе на другой раздел
// (смена pathname → новый key) ошибка сбрасывается и экран рендерится заново.
function RoutedErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

function LegalGate({ children }: { children: React.ReactNode }) {
  const { needsAcceptance } = useLegalAcceptance();
  if (needsAcceptance) {
    return <LegalAcceptScreen />;
  }
  return <>{children}</>;
}

function SubscriptionGuard() {
  const { profile } = useUserStore();
  const location = useLocation();
  const navigate = useNavigate();
  // '/gift' открыт и с истёкшей подпиской: подарить другу можно всегда.
  // '/gift/welcome' — иначе получателя выкинет на paywall до того, как
  // обновлённый профиль долетит до стора.
  // /test — лид-магнит: пройти тест можно и без подписки.
  // /start-review и /portrait сами показывают замок и ведут на оплату (30.10.2026).
  const ALLOWED_PATHS = ['/paywall', '/profile', '/admin', '/gift', '/gift/welcome', '/test', '/start-review', '/portrait'];
  useEffect(() => {
    if (!profile) return;
    const status = profile.subscription_status;
    const expiresAt = profile.subscription_expires_at
      ? new Date(profile.subscription_expires_at)
      : null;
    const isExpired = expiresAt ? expiresAt < new Date() : false;
    const blocked =
      status === 'expired' ||
      status === 'canceled' ||
      (status === 'trial' && isExpired) ||
      (status === 'active' && isExpired);
    if (blocked && !ALLOWED_PATHS.includes(location.pathname)) {
      navigate('/paywall', { replace: true });
    }
  }, [profile, location.pathname]);
  return null;
}

function PromoCodeApplier() {
  const { profile, setProfile } = useUserStore();
  useEffect(() => {
    if (!profile?.id) return;
    const params = new URLSearchParams(window.location.search);
    const promoCode = params.get('promo');
    if (!promoCode) return;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('apply_promo_code', {
          promo_text: promoCode,
        });
        if (error || !data) {
          alert('Не удалось применить промо-код');
          cleanUrl();
          return;
        }
        if (!data.success) {
          const errMsg: Record<string, string> = {
            invalid: 'Промо-код недействителен или отключён',
            already_used: 'Ты уже использовал этот промо-код',
            exhausted: 'Промо-код уже исчерпан',
            not_authenticated: 'Сначала войди в приложение',
          };
          alert(errMsg[data.error] || 'Ошибка применения промо-кода');
          cleanUrl();
          return;
        }
        const { data: updatedProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', profile.id)
          .single();
        if (updatedProfile) setProfile(updatedProfile);
        const durLabel = data.duration_days >= 36500
          ? 'Навсегда'
          : `${data.duration_days} дней`;
        alert(`🎉 Промо-код применён! Подписка активна: ${durLabel}`);
        cleanUrl();
      } catch (e) {
        console.error('Promo code error:', e);
        cleanUrl();
      }
    })();
    function cleanUrl() {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [profile?.id]);
  return null;
}

// Ловит код подарка (?gift= в вебе или Telegram start_param 'gift_КОД') и активирует.
// Все проверки — в RPC redeem_gift (свой подарок себе нельзя, повторно нельзя,
// неоплаченный нельзя), поэтому здесь только вызов и обновление профиля.
// Payload приветственного экрана кладём в localStorage: он обязан пережить
// онбординг нового пользователя и перезапуск мини-аппа.
function GiftApplier() {
  const { profile, setProfile } = useUserStore();
  const navigate = useNavigate();
  useEffect(() => {
    if (!profile?.id) return;
    const params = new URLSearchParams(window.location.search);
    let raw = params.get('gift');
    const sp = (window as any).Telegram?.WebApp?.initDataUnsafe?.start_param;
    if (!raw && typeof sp === 'string' && sp.startsWith('gift_')) raw = sp.slice(5);
    if (!raw) {
      try {
        raw = sessionStorage.getItem(PENDING_GIFT_KEY);
      } catch {
        raw = null;
      }
    }
    if (!raw) return;
    const code = raw;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('redeem_gift', { p_code: code });
        if (error || !data?.success) {
          console.error('Gift redeem failed:', error || data?.error);
          return;
        }
        localStorage.setItem(
          GIFT_WELCOME_KEY,
          JSON.stringify({
            code: data.code,
            giver_name: data.giver_name,
            note: data.note ?? null,
            days: data.days,
            plan: data.plan,
          }),
        );
        // Подписка только что стала активной — обновляем стор,
        // иначе SubscriptionGuard унесёт человека на paywall.
        const { data: updated } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', profile.id)
          .single();
        if (updated) setProfile(updated);
        // Весточка дарителю «подарок открыли». Уходит ровно один раз на подарок,
        // сбой тут не должен мешать человеку войти.
        supabase.functions
          .invoke('gift-redeemed', { body: { code: data.code } })
          .catch((e) => console.error('gift-redeemed notify failed:', e));
        navigate('/gift/welcome', { replace: true });
      } catch (e) {
        console.error('Gift apply error:', e);
      } finally {
        try {
          sessionStorage.removeItem(PENDING_GIFT_KEY);
        } catch { /* нечего чистить */ }
        const url = new URL(window.location.href);
        url.searchParams.delete('gift');
        window.history.replaceState({}, '', url.pathname + url.search);
      }
    })();
  }, [profile?.id]);
  return null;
}

// Подарок могли активировать во время онбординга — тогда приветственный экран
// показать было негде. Показываем при первой возможности.
function GiftWelcomeGate() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (location.pathname === '/gift/welcome') return;
    if (readGiftWelcome()) navigate('/gift/welcome', { replace: true });
  }, [location.pathname]);
  return null;
}

// Ловит реф-код (?ref= в вебе или Telegram start_param 'ref_КОД') и применяет.
// RPC сам гвардит (не сам себя / не повторно / только новый юзер) — тихий фоновый бонус.
// Ссылка теста (?startapp=test) у того, кто уже в приложении, — сразу на экран
// теста, один раз за запуск. Новичка этим займётся онбординг: у него тест идёт первым.
function LaunchRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    try {
      const l = getLaunch();
      // launch_done ставит и онбординг: новичок по ссылке теста проходит тест
      // внутри онбординга, второй раз его туда отправлять нельзя.
      if (!l || sessionStorage.getItem('launch_done')) return;
      sessionStorage.setItem('launch_done', '1');
      if (l.feature === 'listen') sessionStorage.setItem('dofi_listen', '1');
      navigate(FEATURE_ROUTE[l.feature]);
    } catch {}
  }, [navigate]);
  return null;
}

// Метка ссылки продвижения — в профиль новичка, один раз (миграция 049).
// Только если профиль моложе суток: старый пользователь, открывший ссылку
// из поста, не должен записываться как «пришёл с этого поста».
function LaunchAttribution() {
  const { profile, setProfile } = useUserStore();
  useEffect(() => {
    const l = getLaunch();
    if (!l || !profile?.id || (profile as any).acquisition) return;
    const created = (profile as any).created_at ? new Date((profile as any).created_at).getTime() : 0;
    if (!created || Date.now() - created > 86400000) return;
    const acquisition = { raw: l.raw, feature: l.feature, label: l.label, at: new Date().toISOString() };
    supabase.from('profiles').update({ acquisition }).eq('id', profile.id).then(({ error }) => {
      if (error) console.warn('[LaunchAttribution]', error);
      else setProfile({ ...profile, acquisition } as any);
    });
  }, [profile?.id]);
  return null;
}

function ReferralApplier() {
  const { profile } = useUserStore();
  useEffect(() => {
    if (!profile?.id) return;
    const params = new URLSearchParams(window.location.search);
    let code = params.get('ref');
    const sp = (window as any).Telegram?.WebApp?.initDataUnsafe?.start_param;
    if (!code && typeof sp === 'string' && sp.startsWith('ref_')) code = sp.slice(4);
    if (!code) return;
    (async () => {
      try {
        await supabase.rpc('apply_referral', { p_ref_code: code });
      } catch (e) {
        console.error('Referral apply error:', e);
      } finally {
        const url = new URL(window.location.href);
        url.searchParams.delete('ref');
        window.history.replaceState({}, '', url.pathname + url.search);
      }
    })();
  }, [profile?.id]);
  return null;
}
